import { useEffect, useState } from 'react'
import { AdminBetaDashboard } from './components/admin/AdminBetaDashboard'
import { CookieBanner } from './components/layout/CookieBanner'
import { KnowledgeAnalysisPage } from './components/demo/KnowledgeAnalysisPage'
import { Footer } from './components/layout/Footer'
import { Header } from './components/layout/Header'
import { LegalPage, type LegalRoute } from './components/legal/LegalPage'
import { AiAssistantSection } from './components/sections/AiAssistantSection'
import { FoundingBetaSection } from './components/sections/FoundingBetaSection'
import { HeroSection } from './components/sections/HeroSection'
import { HowItWorksSection } from './components/sections/HowItWorksSection'
import { InteractiveDemoSection } from './components/sections/InteractiveDemoSection'
import { RoadmapSection } from './components/sections/RoadmapSection'
import { SupportedHardwareSection } from './components/sections/SupportedHardwareSection'
import { WhyToneSignatureSection } from './components/sections/WhyToneSignatureSection'
import { useRevealAnimation } from './hooks/useRevealAnimation'
import type { Language } from './models/LandingPage'
import { initializeAnalytics } from './utils/analytics'
import { copy } from './utils/landingContent'
import { setPageMetadata } from './utils/seo'

const legalRoutes = ['/privacy', '/cookies', '/terms'] as const
type LegalPath = typeof legalRoutes[number]

export default function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const stored = window.localStorage.getItem('tonesignature.language')
    return stored === 'it' ? 'it' : 'en'
  })
  const [path, setPath] = useState(() => window.location.pathname)
  const reveal = useRevealAnimation()
  const t = copy[language]
  const legalPath = legalRoutes.includes(path as LegalPath) ? path as LegalPath : null
  const isAdminPath = path === '/admin/beta'
  const analysisMatch = path.match(/^\/analysis\/([^/]+)$/)
  const analysisSongId = analysisMatch?.[1] ? decodeURIComponent(analysisMatch[1]) : null

  useEffect(() => {
    document.documentElement.lang = language
    window.localStorage.setItem('tonesignature.language', language)
  }, [language])

  useEffect(() => {
    function handlePopState() {
      setPath(window.location.pathname)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    initializeAnalytics()
  }, [])

  useEffect(() => {
    if (legalPath || isAdminPath || analysisSongId) {
      return
    }

    setPageMetadata({
      title: 'ToneSignature - The AI That Understands Guitar Tone',
      description: 'Turn any song into a native preset for your guitar hardware. ToneSignature analyzes guitar tone and recreates it for Helix and future supported devices.',
      canonical: 'https://tonesignature.com/',
    })
  }, [legalPath, isAdminPath, analysisSongId])

  if (isAdminPath) {
    return <AdminBetaDashboard/>
  }

  if (analysisSongId) {
    return <div id="top">
      <Header copy={t} language={language} onLanguageChange={setLanguage}/>
      <KnowledgeAnalysisPage songId={analysisSongId}/>
      <Footer language={language}/>
      <CookieBanner language={language}/>
    </div>
  }

  return <div id="top">
    <Header copy={t} language={language} onLanguageChange={setLanguage}/>

    {legalPath ? <LegalPage page={legalPath.slice(1) as LegalRoute} language={language}/> : <main>
      <HeroSection copy={t}/>
      <HowItWorksSection copy={t} reveal={reveal}/>
      <InteractiveDemoSection copy={t} language={language} reveal={reveal}/>
      <WhyToneSignatureSection copy={t} reveal={reveal}/>
      <AiAssistantSection copy={t} reveal={reveal}/>
      <SupportedHardwareSection copy={t} reveal={reveal}/>
      <RoadmapSection copy={t} reveal={reveal}/>
      <FoundingBetaSection copy={t} language={language} reveal={reveal}/>
    </main>}

    <Footer language={language}/>
    <CookieBanner language={language}/>
  </div>
}
